import { useMemo } from 'react';
import { scaleLinear, line, curveStepAfter } from 'd3';
import { Chart } from './Chart';
import { driverColor } from './colors';

function buildSeries(laps) {
  const byDriver = new Map();
  for (const l of laps) {
    if (l.is_pit_in || l.is_pit_out) continue;
    const t = Number(l.lap_time_seconds);
    if (!isFinite(t) || t <= 0) continue;
    const lap = Number(l.lap_number);
    if (!byDriver.has(l.driver)) byDriver.set(l.driver, []);
    byDriver.get(l.driver).push({ lap, t });
  }
  const series = [];
  for (const [driver, points] of byDriver.entries()) {
    points.sort((a, b) => a.lap - b.lap);
    let best = Infinity;
    const stair = [];
    for (const p of points) {
      if (p.t < best) best = p.t;
      stair.push({ lap: p.lap, t: best });
    }
    if (stair.length === 0) continue;
    series.push({ driver, points: stair, finalBest: stair[stair.length - 1].t });
  }
  series.sort((a, b) => a.finalBest - b.finalBest);
  return series;
}

const HIGHLIGHT_N = 5;

export const ImprovementTrajectory = ({ laps }) => {
  const series = useMemo(() => buildSeries(laps || []), [laps]);

  if (series.length === 0) {
    return (
      <div className="absolute inset-0 grid place-items-center text-xs text-f1-muted px-6 text-center">
        No qualifying laps available.
      </div>
    );
  }

  return (
    <Chart>
      {({ width, height }) => {
        const margin = { top: 6, right: 56, bottom: 18, left: 32 };
        const innerW = Math.max(0, width - margin.left - margin.right);
        const innerH = Math.max(0, height - margin.top - margin.bottom);

        const allLaps = series.flatMap((s) => s.points.map((p) => p.lap));
        const allTimes = series.flatMap((s) => s.points.map((p) => p.t));
        const xMin = Math.min(...allLaps), xMax = Math.max(...allLaps);
        const yMin = Math.min(...allTimes), yMax = Math.max(...allTimes);
        const yPad = (yMax - yMin) * 0.05 || 0.5;

        const x = scaleLinear().domain([xMin, xMax]).range([0, innerW]);
        const y = scaleLinear().domain([yMin - yPad, yMax + yPad]).range([0, innerH]);

        const drivers = series.map((s) => s.driver);
        const top = series.slice(0, HIGHLIGHT_N);
        const rest = series.slice(HIGHLIGHT_N);

        const stepLine = line()
          .x((p) => x(p.lap))
          .y((p) => y(p.t))
          .curve(curveStepAfter);

        return (
          <svg width={width} height={height}>
            <g transform={`translate(${margin.left},${margin.top})`}>
              {/* faint ghost lines for non-top drivers */}
              {rest.map((s) => (
                <path
                  key={s.driver}
                  d={stepLine(s.points) || ''}
                  fill="none"
                  stroke="#3f3f46"
                  strokeWidth={1}
                  opacity={0.7}
                >
                  <title>
                    {s.driver}
                    {`\nfinal best: ${s.finalBest.toFixed(3)}s`}
                    {`\nunlocked on lap ${s.points.find((p) => p.t === s.finalBest)?.lap ?? '?'}`}
                  </title>
                </path>
              ))}
              {/* highlighted top-N */}
              {top.map((s) => {
                const color = driverColor(s.driver, drivers);
                return (
                  <g key={s.driver}>
                    <path
                      d={stepLine(s.points) || ''}
                      fill="none"
                      stroke={color}
                      strokeWidth={1.5}
                      opacity={0.95}
                    >
                      <title>
                        {s.driver}
                        {`\nfinal best: ${s.finalBest.toFixed(3)}s`}
                        {`\nunlocked on lap ${s.points.find((p) => p.t === s.finalBest)?.lap ?? '?'}`}
                      </title>
                    </path>
                    <text
                      x={x(s.points[s.points.length - 1].lap) + 4}
                      y={y(s.finalBest) + 3}
                      fontSize="9"
                      fill={color}
                    >
                      {s.driver} {s.finalBest.toFixed(2)}
                    </text>
                  </g>
                );
              })}

              {/* axes */}
              <text x={0} y={innerH + 12} fontSize="9" fill="#6b7280">L{xMin}</text>
              <text x={innerW} y={innerH + 12} textAnchor="end" fontSize="9" fill="#6b7280">
                L{xMax}
              </text>
              <text x={-4} y={4} textAnchor="end" fontSize="9" fill="#6b7280">
                {yMin.toFixed(2)}
              </text>
              <text x={-4} y={innerH} textAnchor="end" fontSize="9" fill="#6b7280">
                {yMax.toFixed(2)}
              </text>
            </g>
          </svg>
        );
      }}
    </Chart>
  );
};
